import { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { useToast } from "hooks/toast";
import { useAppSelector } from "store/rootReducer";
import api from "services/api";
import { HearingService } from "services/hearing";

type Params = {
  hearingId?: number | string | null;
  hearingFileId?: number | string | null;
  audienceNumber?: string | null;
  audienceVideo?: string | null;
  placeId?: string | null;
};

type Result = {
  isReady: boolean;
  isAllowed: boolean;
  audienceUnitId?: number;
};

function toNumber(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function parseUnitFromVideoUrl(url?: string | null): number | undefined {
  if (!url) return;
  const m = /unidade_(\d+)/i.exec(url);
  return m ? toNumber(m[1]) : undefined;
}

async function getHearingIdFromHearingFileId(hearingFileId: number | string) {
  try {
    const { data } = await api.get(
      `/v1/HearingIndex/GetAllByHearingFileId?hearingFileId=${hearingFileId}`,
    );
    return toNumber(data?.data?.[0]?.hearingId);
  } catch {
    return undefined;
  }
}

async function getHearingPayloadById(id: number) {
  try {
    const resp = await HearingService.getById(id);
    return resp?.data?.data ?? resp?.data ?? resp;
  } catch {
    return undefined;
  }
}

async function getHearingPayloadByNumber(audienceNumber: string) {
  try {
    const resp = await api.get(`/v1/Hearing/GetByNumber`, {
      params: { audienceNumber },
    });
    return resp?.data?.data ?? resp?.data ?? resp;
  } catch {
    return undefined;
  }
}

function extractUnitId(payload: any): number | undefined {
  return (
    toNumber(payload?.unitId) ??
    toNumber(payload?.placeId) ??
    toNumber(payload?.varaId) ??
    toNumber(payload?.unit?.id) ??
    toNumber(payload?.place?.id)
  );
}

function getUserPlaceIds(profile: any): number[] {
  const fromPlaces = Array.isArray(profile?.places)
    ? profile.places.filter((p: any) => p?.active !== false)
    : [];
  const base: number[] = [];
  if (Array.isArray(profile?.units))
    base.push(
      ...(profile.units
        .map((u: any) => toNumber(u?.id ?? u))
        .filter(Boolean) as number[]),
    );
  if (Array.isArray(profile?.allowedUnits))
    base.push(
      ...(profile.allowedUnits
        .map((u: any) => toNumber(u?.id ?? u))
        .filter(Boolean) as number[]),
    );
  if (fromPlaces.length)
    base.push(
      ...(fromPlaces
        .map((p: any) => toNumber(p?.id))
        .filter(Boolean) as number[]),
    );
  if (profile?.placeId) base.push(toNumber(profile.placeId)!);
  return Array.from(
    new Set(base.filter((n) => Number.isFinite(n))),
  ) as number[];
}

export function usePlaceGate(params: Params): Result {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { userCustomerProfile } = useAppSelector((s) => s.profile);

  const userPlaceIds = useMemo(
    () => getUserPlaceIds(userCustomerProfile),
    [userCustomerProfile],
  );

  const [isReady, setIsReady] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [audienceUnitId, setAudienceUnitId] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    let active = true;

    async function run() {
      let unitId: number | undefined;

      if (userPlaceIds.includes(Number(params.placeId))) {
        const payload = await getHearingPayloadById(Number(params.placeId));
        unitId = Number(params.placeId);
      }

      if (params.hearingId) {
        const payload = await getHearingPayloadById(Number(params.hearingId));
        unitId = extractUnitId(payload);
      } else if (params.hearingFileId) {
        const hid = await getHearingIdFromHearingFileId(params.hearingFileId);
        if (hid) {
          const payload = await getHearingPayloadById(hid);
          unitId = extractUnitId(payload);
        }
      } else if (params.audienceNumber) {
        const payload = await getHearingPayloadByNumber(params.audienceNumber);
        unitId = extractUnitId(payload);
      }

      if (!unitId && params.audienceVideo) {
        unitId = parseUnitFromVideoUrl(params.audienceVideo);
      }
      const allowed = unitId ? userPlaceIds.includes(unitId) : false;

      if (!active) return;

      setAudienceUnitId(unitId);
      setIsAllowed(allowed);
      setIsReady(true);

      if (!allowed) {
        addToast({
          type: "error",
          title: "Acesso bloqueado",
          description: t("audiences.messages.notBelongsToUnit"),
        });
        navigate("/dashboard", { replace: true });
      }
    }

    run();
    return () => {
      active = false;
    };
  }, [
    params.hearingId,
    params.hearingFileId,
    params.audienceNumber,
    params.audienceVideo,
    userPlaceIds,
    addToast,
    navigate,
  ]);

  return { isReady, isAllowed, audienceUnitId };
}
